import * as THREE from "three";
import { animateRunner, createRacer, paintRacer } from "./racer.js";

const PLATFORM_COLORS = {
  normal: 0xf8d36a,
  moving: 0x7dd7e8,
  bounce: 0xf68a91,
  shelter: 0x77c98d,
  finish: 0xfff5c4,
};

export class World {
  constructor(canvas) {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x85dded);
    this.scene.fog = new THREE.Fog(0x85dded, 40, 150);
    this.camera = new THREE.PerspectiveCamera(55, innerWidth / innerHeight, 0.1, 260);
    this.camera.position.set(8, 8, -13);
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: "high-performance" });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.setSize(innerWidth, innerHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.08;

    this.clockTime = 0;
    this.courseGroup = new THREE.Group();
    this.remoteGroup = new THREE.Group();
    this.decorGroup = new THREE.Group();
    this.scene.add(this.courseGroup, this.remoteGroup, this.decorGroup);
    this.platformMeshes = new Map();
    this.remotePlayers = new Map();
    this.course = [];

    this.buildLights();
    this.buildOcean();
    this.buildBackdrop();

    addEventListener("resize", () => this.resize());
  }

  buildLights() {
    const hemisphere = new THREE.HemisphereLight(0xe9fbff, 0x5f8772, 2.1);
    this.scene.add(hemisphere);
    const sun = new THREE.DirectionalLight(0xfff0c2, 3.2);
    sun.position.set(-24, 38, -18);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.left = -40;
    sun.shadow.camera.right = 40;
    sun.shadow.camera.top = 55;
    sun.shadow.camera.bottom = -25;
    sun.shadow.camera.far = 120;
    this.scene.add(sun);
  }

  buildOcean() {
    const geometry = new THREE.PlaneGeometry(320, 320, 24, 24);
    const material = new THREE.MeshPhysicalMaterial({
      color: 0x168dcc,
      roughness: 0.2,
      metalness: 0.05,
      transparent: true,
      opacity: 0.86,
      transmission: 0.08,
      clearcoat: 0.6,
      flatShading: true,
    });
    this.water = new THREE.Mesh(geometry, material);
    this.water.rotation.x = -Math.PI / 2;
    this.water.position.y = -5;
    this.water.receiveShadow = true;
    this.scene.add(this.water);
    this.waterBase = new Float32Array(geometry.attributes.position.array);

    this.waterLines = new THREE.Group();
    const foamMaterial = new THREE.MeshBasicMaterial({ color: 0xe7fbff, transparent: true, opacity: 0.55 });
    for (let index = 0; index < 36; index += 1) {
      const line = new THREE.Mesh(
        new THREE.BoxGeometry(1.4 + (index % 5) * 0.7, 0.045, 0.12),
        foamMaterial,
      );
      line.position.set(((index * 37) % 90) - 45, 0.08, ((index * 19) % 90) - 45);
      line.rotation.y = index * 0.7;
      this.waterLines.add(line);
    }
    for (let index = 0; index < 10; index += 1) {
      const ripple = new THREE.Mesh(
        new THREE.TorusGeometry(0.8 + (index % 3) * 0.35, 0.035, 6, 18),
        foamMaterial,
      );
      ripple.rotation.x = Math.PI / 2;
      ripple.position.set(((index * 23) % 70) - 35, 0.05, ((index * 29) % 70) - 35);
      ripple.userData.ripple = index;
      this.waterLines.add(ripple);
    }
    this.water.add(this.waterLines);
  }

  buildBackdrop() {
    const islandMaterial = new THREE.MeshStandardMaterial({ color: 0x5ab77b, flatShading: true, roughness: 1 });
    const sandMaterial = new THREE.MeshStandardMaterial({ color: 0xe7d7a4, flatShading: true, roughness: 1 });
    const rockMaterial = new THREE.MeshStandardMaterial({ color: 0x8a9a91, flatShading: true });
    for (let index = 0; index < 18; index += 1) {
      const angle = (index / 18) * Math.PI * 2;
      const radius = 48 + (index % 4) * 9;
      const material = index % 5 === 1 ? sandMaterial : index % 3 === 0 ? rockMaterial : islandMaterial;
      const island = new THREE.Mesh(
        new THREE.ConeGeometry(5 + (index % 3), 10 + (index % 5), 7),
        material,
      );
      island.position.set(Math.cos(angle) * radius, -2, Math.sin(angle) * radius + 20);
      island.rotation.y = angle;
      if (index % 3 !== 0) addPalm(island, 0.4, 6.2, 0.2);
      this.decorGroup.add(island);
    }

    const cloudMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.8 });
    for (let index = 0; index < 10; index += 1) {
      const cloud = new THREE.Group();
      for (let puff = 0; puff < 4; puff += 1) {
        const sphere = new THREE.Mesh(new THREE.IcosahedronGeometry(2.2 + (puff % 2), 1), cloudMaterial);
        sphere.position.set(puff * 2.5, Math.sin(puff) * 0.7, 0);
        cloud.add(sphere);
      }
      cloud.position.set((index - 5) * 18, 22 + (index % 3) * 8, 15 + (index % 4) * 18);
      cloud.scale.setScalar(0.7 + (index % 2) * 0.25);
      this.decorGroup.add(cloud);
    }

    for (const side of [-1, 1]) {
      addBoat(this.decorGroup, side * 16, -1.2, 8 + side * 6);
      addBuoy(this.decorGroup, side * 11, -0.2, 18);
      addBuoy(this.decorGroup, side * 22, -0.35, 34);
    }
  }

  loadCourse(course) {
    this.course = course;
    this.platformMeshes.clear();
    while (this.courseGroup.children.length) {
      this.courseGroup.remove(this.courseGroup.children[0]);
    }

    const sideMaterial = new THREE.MeshStandardMaterial({ color: 0xc17d45, flatShading: true, roughness: 1 });
    course.forEach((platform, index) => {
      const group = new THREE.Group();
      group.position.set(platform.x, platform.y, platform.z);
      group.userData.platform = platform;

      const topMaterial = new THREE.MeshStandardMaterial({
        color: PLATFORM_COLORS[platform.type] ?? PLATFORM_COLORS.normal,
        flatShading: true,
        roughness: 0.88,
      });
      const base = new THREE.Mesh(new THREE.BoxGeometry(platform.width, 0.75, platform.depth), sideMaterial);
      base.position.y = -0.42;
      base.castShadow = true;
      base.receiveShadow = true;
      group.add(base);

      const top = new THREE.Mesh(new THREE.BoxGeometry(platform.width + 0.08, 0.16, platform.depth + 0.08), topMaterial);
      top.position.y = 0.04;
      top.castShadow = true;
      top.receiveShadow = true;
      group.add(top);
      this.addDeckDressing(group, platform, index);

      if (platform.type === "shelter") this.addCheckpoint(group, platform);
      if (platform.type === "bounce") this.addBouncePad(group, platform);
      if (platform.type === "finish" || index === course.length - 1) this.addFinishBeacon(group);
      if (platform.hasPickup) this.addPickup(group, platform);

      this.courseGroup.add(group);
      this.platformMeshes.set(platform.id ?? index, group);
    });
  }

  addDeckDressing(group, platform, index) {
    const groove = new THREE.MeshStandardMaterial({ color: 0x9a6234, flatShading: true, roughness: 1 });
    const pilingMaterial = new THREE.MeshStandardMaterial({ color: 0x7a4b2e, flatShading: true, roughness: 1 });
    for (let plank = 0; plank < 4; plank += 1) {
      const board = new THREE.Mesh(new THREE.BoxGeometry(platform.width * 0.9, 0.025, 0.055), groove);
      board.position.set(0, 0.135, (plank - 1.5) * platform.depth * 0.2);
      group.add(board);
    }
    for (const x of [-1, 1]) {
      for (const z of [-1, 1]) {
        const piling = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.12, 1.35, 6), pilingMaterial);
        piling.position.set(x * platform.width * 0.4, -0.95, z * platform.depth * 0.36);
        piling.castShadow = true;
        group.add(piling);
      }
    }
    if (platform.type === "moving") {
      const mark = new THREE.MeshStandardMaterial({ color: 0xfffdf6, flatShading: true });
      for (const z of [-1, 1]) {
        const stripe = new THREE.Mesh(new THREE.BoxGeometry(platform.width * 0.55, 0.03, 0.16), mark);
        stripe.position.set(0, 0.15, z * platform.depth * 0.28);
        group.add(stripe);
      }
    }
    if (index % 3 === 1 && platform.type === "normal" && !platform.hasPickup) {
      const crate = new THREE.Mesh(
        new THREE.BoxGeometry(0.55, 0.45, 0.55),
        new THREE.MeshStandardMaterial({ color: 0xc9844a, flatShading: true }),
      );
      crate.position.set(platform.width * 0.22, 0.38, -platform.depth * 0.18);
      crate.castShadow = true;
      group.add(crate);
    }
  }

  addCheckpoint(group, platform) {
    const wood = new THREE.MeshStandardMaterial({ color: 0xfff6d8, flatShading: true });
    const green = new THREE.MeshStandardMaterial({
      color: 0x3cbf7a,
      emissive: 0x0d5a32,
      emissiveIntensity: 0.28,
      flatShading: true,
    });
    const cream = new THREE.MeshStandardMaterial({ color: 0xfffdf8, flatShading: true });
    const safeStripe = new THREE.Mesh(new THREE.BoxGeometry(platform.width * 0.72, 0.04, 0.34), green);
    safeStripe.position.y = 0.16;
    group.add(safeStripe);

    for (const x of [-1, 1]) {
      for (const z of [-1, 1]) {
        const bollard = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.14, 0.62, 8), wood);
        bollard.position.set(x * platform.width * 0.34, 0.42, z * platform.depth * 0.3);
        bollard.castShadow = true;
        group.add(bollard);
        const cap = new THREE.Mesh(new THREE.SphereGeometry(0.13, 8, 6), green);
        cap.position.set(x * platform.width * 0.34, 0.76, z * platform.depth * 0.3);
        group.add(cap);
      }
    }

    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.48, 0.11, 8, 18), green);
    ring.position.set(-platform.width * 0.16, 1.2, -platform.depth * 0.18);
    ring.userData.lifeRing = true;
    ring.castShadow = true;
    group.add(ring);
    const band = new THREE.Mesh(new THREE.TorusGeometry(0.48, 0.035, 6, 18), cream);
    band.position.copy(ring.position);
    band.userData.lifeRing = true;
    group.add(band);

    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.07, 1.7, 6), wood);
    pole.position.set(platform.width * 0.24, 1.0, platform.depth * 0.05);
    pole.castShadow = true;
    group.add(pole);
    const lamp = new THREE.Mesh(
      new THREE.SphereGeometry(0.2, 10, 8),
      new THREE.MeshStandardMaterial({ color: 0xd8ffe8, emissive: 0x3ee08a, emissiveIntensity: 1.15 }),
    );
    lamp.position.set(platform.width * 0.24, 1.95, platform.depth * 0.05);
    lamp.userData.safeLamp = true;
    group.add(lamp);
  }

  addBouncePad(group, platform) {
    const pad = new THREE.Mesh(
      new THREE.CylinderGeometry(Math.min(platform.width, platform.depth) * 0.28, Math.min(platform.width, platform.depth) * 0.32, 0.2, 16),
      new THREE.MeshStandardMaterial({ color: 0xff5a7a, emissive: 0x5a1022, emissiveIntensity: 0.15 }),
    );
    pad.position.y = 0.22;
    pad.castShadow = true;
    pad.userData.bouncePad = true;
    group.add(pad);
  }

  addPickup(group) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.42, 0.12, 8, 20),
      new THREE.MeshStandardMaterial({ color: 0xffef68, emissive: 0x9a6b00, emissiveIntensity: 0.35 }),
    );
    ring.position.y = 1.15;
    ring.rotation.x = Math.PI / 2;
    ring.userData.pickup = true;
    group.add(ring);
  }

  addFinishBeacon(group) {
    const pole = new THREE.Mesh(
      new THREE.CylinderGeometry(0.09, 0.12, 5, 8),
      new THREE.MeshStandardMaterial({ color: 0xf8f5e8 }),
    );
    pole.position.y = 2.5;
    group.add(pole);
    const light = new THREE.Mesh(
      new THREE.SphereGeometry(0.48, 12, 8),
      new THREE.MeshStandardMaterial({ color: 0xffdd43, emissive: 0xff9d00, emissiveIntensity: 1.5 }),
    );
    light.position.y = 5.1;
    light.userData.beacon = true;
    group.add(light);
  }

  createPlayer(color = "#ff7b35", isLocal = false) {
    const group = createRacer(color, { parent: isLocal ? this.scene : this.remoteGroup });
    group.userData.isLocal = isLocal;
    return group;
  }

  setRemotePlayers(players, localId) {
    const active = new Set();
    for (const player of players) {
      if (player.id === localId || !player.position) continue;
      active.add(player.id);
      let mesh = this.remotePlayers.get(player.id);
      if (!mesh) {
        mesh = this.createPlayer(player.color ?? "#ff7b35");
        this.remotePlayers.set(player.id, mesh);
      } else if (player.color) {
        paintRacer(mesh, player.color);
      }
      mesh.visible = !player.eliminated && !player.finished;
      mesh.userData.targetPosition.set(player.position.x, player.position.y, player.position.z);
      mesh.userData.targetYaw = player.position.yaw ?? 0;
    }
    for (const [id, mesh] of this.remotePlayers) {
      if (!active.has(id)) {
        this.remoteGroup.remove(mesh);
        this.remotePlayers.delete(id);
      }
    }
  }

  platformTransform(platform, elapsed = this.clockTime) {
    let x = platform.x;
    let y = platform.y;
    let z = platform.z;
    if (platform.type === "moving") {
      const axis = platform.moveAxis ?? (Number(platform.id) % 2 ? "x" : "z");
      const offset = Math.sin(elapsed * (platform.moveSpeed ?? 1.2) + (platform.phase ?? Number(platform.id) ?? 0)) * (platform.moveRange ?? 1.8);
      if (axis === "x") x += offset;
      else z += offset;
    }
    return { x, y, z };
  }

  update(elapsed, delta, waterHeight) {
    this.clockTime = elapsed;
    this.water.position.y += (waterHeight - this.water.position.y) * Math.min(1, delta * 4);
    this.water.position.x = Math.sin(elapsed * 0.16) * 2;
    const positions = this.water.geometry.attributes.position;
    for (let index = 0; index < positions.count; index += 1) {
      const x = this.waterBase[index * 3];
      const y = this.waterBase[index * 3 + 1];
      positions.setZ(
        index,
        Math.sin(elapsed * 1.25 + x * 0.16) * 0.16 + Math.cos(elapsed * 0.85 + y * 0.13) * 0.1,
      );
    }
    positions.needsUpdate = true;
    this.waterLines.children.forEach((line, index) => {
      if (line.userData.ripple !== undefined) {
        const pulse = 1 + Math.sin(elapsed * 1.4 + line.userData.ripple) * 0.18;
        line.scale.setScalar(pulse);
        return;
      }
      line.position.x += Math.sin(elapsed * 0.8 + index) * delta * 0.2;
    });

    for (const platform of this.course) {
      const mesh = this.platformMeshes.get(platform.id);
      if (!mesh) continue;
      const transform = this.platformTransform(platform, elapsed);
      mesh.position.set(transform.x, transform.y, transform.z);
      for (const child of mesh.children) {
        if (child.userData.pickup) {
          child.rotation.z = elapsed * 1.8;
          child.position.y = 1.18 + Math.sin(elapsed * 3 + Number(platform.id)) * 0.15;
        }
        if (child.userData.beacon) child.scale.setScalar(1 + Math.sin(elapsed * 4) * 0.12);
        if (child.userData.safeLamp) child.scale.setScalar(1 + Math.sin(elapsed * 3) * 0.08);
        if (child.userData.lifeRing) child.rotation.y = elapsed * 0.6;
        if (child.userData.bouncePad) child.scale.y = 1 + Math.sin(elapsed * 5) * 0.12;
      }
    }
    for (const mesh of this.remotePlayers.values()) {
      mesh.position.lerp(mesh.userData.targetPosition, 1 - Math.exp(-delta * 12));
      mesh.rotation.y = lerpAngle(mesh.rotation.y, mesh.userData.targetYaw ?? 0, 1 - Math.exp(-delta * 10));
      animateRunner(mesh, elapsed, 0.55, true);
    }
    this.decorGroup.rotation.y = Math.sin(elapsed * 0.025) * 0.06;
  }

  updateCamera(target, yaw, delta) {
    const distance = 11;
    const desired = new THREE.Vector3(
      target.x - Math.sin(yaw) * distance,
      target.y + 6.4,
      target.z - Math.cos(yaw) * distance,
    );
    this.camera.position.lerp(desired, 1 - Math.exp(-delta * 5));
    const look = new THREE.Vector3(
      target.x + Math.sin(yaw) * 2.4,
      target.y + 1.35,
      target.z + Math.cos(yaw) * 2.4,
    );
    this.camera.lookAt(look);
  }

  resize() {
    this.camera.aspect = innerWidth / innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.setSize(innerWidth, innerHeight);
  }

  render() {
    this.renderer.render(this.scene, this.camera);
  }
}

export { animateRunner };

function lerpAngle(from, to, alpha) {
  const delta = Math.atan2(Math.sin(to - from), Math.cos(to - from));
  return from + delta * alpha;
}

function addPalm(parent, x, y, z) {
  const trunk = new THREE.Mesh(
    new THREE.CylinderGeometry(0.12, 0.2, 2.4, 6),
    new THREE.MeshStandardMaterial({ color: 0x8d5a32, flatShading: true }),
  );
  trunk.position.set(x, y, z);
  parent.add(trunk);
  const frond = new THREE.MeshStandardMaterial({ color: 0x2f9d5b, flatShading: true });
  for (let index = 0; index < 5; index += 1) {
    const leaf = new THREE.Mesh(new THREE.ConeGeometry(0.28, 1.5, 4), frond);
    leaf.position.set(x, y + 1.15, z);
    leaf.rotation.z = 1.15;
    leaf.rotation.y = (index / 5) * Math.PI * 2;
    parent.add(leaf);
  }
}

function addBoat(parent, x, y, z) {
  const hull = new THREE.Mesh(
    new THREE.BoxGeometry(1.8, 0.45, 3.2),
    new THREE.MeshStandardMaterial({ color: 0xf4efe2, flatShading: true }),
  );
  hull.position.set(x, y, z);
  parent.add(hull);
  const stripe = new THREE.Mesh(
    new THREE.BoxGeometry(1.84, 0.12, 3.24),
    new THREE.MeshStandardMaterial({ color: 0xef6b4a, flatShading: true }),
  );
  stripe.position.set(x, y + 0.12, z);
  parent.add(stripe);
  const cabin = new THREE.Mesh(
    new THREE.BoxGeometry(1.1, 0.7, 1.2),
    new THREE.MeshStandardMaterial({ color: 0x2f6f9f, flatShading: true }),
  );
  cabin.position.set(x, y + 0.55, z + 0.3);
  parent.add(cabin);
}

function addBuoy(parent, x, y, z) {
  const buoy = new THREE.Mesh(
    new THREE.SphereGeometry(0.38, 10, 8),
    new THREE.MeshStandardMaterial({ color: 0xffb703, flatShading: true }),
  );
  buoy.position.set(x, y, z);
  parent.add(buoy);
  const band = new THREE.Mesh(
    new THREE.CylinderGeometry(0.4, 0.4, 0.12, 10),
    new THREE.MeshStandardMaterial({ color: 0xd00000, flatShading: true }),
  );
  band.position.set(x, y, z);
  parent.add(band);
}
